/** @forge-header
 * @id          mlbParsers
 * @role        MLB ticker parsers | game moneyline
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo | kalshiTicker/helpers:parseFallback,mlbTeamFromCode
 * @downstream  kalshiTicker/parse
 * @exports     parseMlbGame:fn
 */

import type { ParsedTicker, GameInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, mlbTeamFromCode } from '../helpers';

/**
 * MLB game moneyline ticker. Two observed shapes:
 *   KXMLBGAME-26APR29NYYTEX-NYY              (no time)
 *   KXMLBGAME-26APR291435NYYTEX-NYY          (with HHMM time)
 *
 * Team codes are variable length (e.g. KC=2, NYY=3, ATH=3) so the trailing
 * -X (the YES team) is used as an anchor instead of fixed offsets.
 */
export function parseMlbGame(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 3) return parseFallback(raw);

  const mid = parts[1] ?? '';
  if (mid.length < 7) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  if (!date) return parseFallback(raw);

  // Strip optional HHMM time block.
  let teamBlob = mid.slice(7);
  if (/^\d{4}/.test(teamBlob)) teamBlob = teamBlob.slice(4);

  const yesTeam = parts[2] ?? '';
  if (!yesTeam) return parseFallback(raw);

  let awayCode: string;
  let homeCode: string;
  if (teamBlob.endsWith(yesTeam) && teamBlob.length > yesTeam.length) {
    awayCode = teamBlob.slice(0, -yesTeam.length);
    homeCode = yesTeam;
  } else if (teamBlob.startsWith(yesTeam) && teamBlob.length > yesTeam.length) {
    awayCode = yesTeam;
    homeCode = teamBlob.slice(yesTeam.length);
  } else {
    return parseFallback(raw);
  }

  if (!awayCode || !homeCode) return parseFallback(raw);

  const game: GameInfo = {
    awayCode,
    homeCode,
    awayName: mlbTeamFromCode(awayCode) ?? undefined,
    homeName: mlbTeamFromCode(homeCode) ?? undefined,
  };

  const shortLabel = `MLB: ${awayCode} @ ${homeCode}`;
  const fullLabel = yesTeam
    ? `MLB: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode} · ${yesTeam} Win`
    : `MLB: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode}`;

  return {
    raw,
    category: 'mlb-game',
    series: 'KXMLBGAME',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: yesTeam,
    shortLabel,
    fullLabel,
  };
}
